// Shared expiry/TTL helpers for the send (ShareFile) and request
// (GenerateLink) pages so the picker logic and the max live in one place.

// Maximum secret/link lifetime in minutes. Substituted at build time by
// the rollup replace plugin (see rollup.config.js, fed from the
// SECURESHARE_MAX_EXPIRY_IN_MINUTES env var). This MUST mirror the
// backend's MAX_EXPIRY_IN_MINUTES env var — the backend is the real
// enforcement and silently clamps to it, so keep the two in sync.
// Falls back to 10080 (7d) — the same default the backend uses — whenever
// the substituted value is missing or not a positive integer (e.g. an
// unsubstituted placeholder in unit tests, or a misconfigured pipeline
// value). Guarding on `> 0` rejects 0, negatives, and NaN, any of which
// would otherwise yield a max expiry of "now" or in the past.
const DEFAULT_MAX_EXPIRY_IN_MINUTES = 10080;
const _configuredMax = parseInt("SECURESHARE_MAX_EXPIRY_IN_MINUTES", 10);
export const MAX_EXPIRY_IN_MINUTES =
	_configuredMax > 0 ? _configuredMax : DEFAULT_MAX_EXPIRY_IN_MINUTES;

// Format an instant as a value for an <input type="datetime-local">,
// which expects local wall-clock time with no timezone suffix.
export function formatLocalDateTime(d) {
	const dt = new Date(d);
	const offset = dt.getTimezoneOffset();
	return new Date(dt.getTime() - offset * 60000).toISOString().slice(0, 16);
}

// Default selection: 1 hour out, but never beyond the configured max.
export function defaultExpiry() {
	const minutes = Math.min(60, MAX_EXPIRY_IN_MINUTES);
	return formatLocalDateTime(Date.now() + minutes * 60000);
}

// The latest selectable instant — drives the datetime input's `max`
// attribute so the user can't pick past the limit in the first place.
export function maxExpiry() {
	return formatLocalDateTime(Date.now() + MAX_EXPIRY_IN_MINUTES * 60000);
}

// Convert a picked datetime back to minutes-from-now for the API,
// clamped to [1, MAX] as a belt-and-suspenders guard alongside the
// input's `max` attribute.
export function expiryToMinutes(expiryDateTime) {
	const ms = new Date(expiryDateTime).getTime() - Date.now();
	const minutes = Math.round(ms / 60000);
	return Math.min(MAX_EXPIRY_IN_MINUTES, Math.max(1, minutes));
}

// Preset shortcut buttons, filtered to those within the configured max
// so we never offer a duration the backend would just clamp away.
const ALL_SHORTCUTS = [
	{ label: "1 hour", ms: 60 * 60 * 1000 },
	{ label: "3 hours", ms: 3 * 60 * 60 * 1000 },
	{ label: "12 hours", ms: 12 * 60 * 60 * 1000 },
	{ label: "1 day", ms: 24 * 60 * 60 * 1000 },
	{ label: "3 days", ms: 3 * 24 * 60 * 60 * 1000 },
	{ label: "1 week", ms: 7 * 24 * 60 * 60 * 1000 },
];

export function expiryShortcuts() {
	const maxMs = MAX_EXPIRY_IN_MINUTES * 60 * 1000;
	return ALL_SHORTCUTS.filter((s) => s.ms <= maxMs);
}
